/**
 * Turns failed requests into messages a field user can act on.
 *
 * Axios reports every transport failure as "Network Error" or
 * "timeout of Nms exceeded", which tells a user on a weak connection nothing.
 * This module tells apart: device offline, connected but no internet, slow
 * internet, server unreachable, server error, and a request the backend
 * rejected (wrong password, etc.), whose own message is shown as-is.
 */

export const ERROR_KIND = {
  OFFLINE: "offline",
  NO_INTERNET: "noInternet",
  SLOW: "slow",
  SERVER_SLOW: "serverSlow",
  SERVER_UNREACHABLE: "serverUnreachable",
  SERVER: "server",
  TOO_MANY: "tooMany",
  REJECTED: "rejected",
  CANCELED: "canceled",
};

// Short messages for the global interceptor, where we cannot afford a probe.
export const NETWORK_MESSAGES = {
  offline:
    "No internet connection. Check your Wi-Fi or mobile data and try again.",
  timeout:
    "The server took too long to respond. Your internet connection may be slow — please try again.",
  unreachable:
    "Can't reach the server. Check your internet connection and try again.",
  session:
    "Your connection is too weak to renew your sign-in right now. It retries automatically — data will load once the connection improves. No need to log out.",
};

export const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

export const isTimeoutError = (error) =>
  error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";

export const isCanceledError = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

/**
 * True when the browser itself reports a slow link. Only Chromium browsers
 * (Chrome, Edge, Android) expose navigator.connection; elsewhere this is false
 * and slowness is detected from timings instead.
 */
export const isConnectionReportedSlow = () => {
  const c = typeof navigator !== "undefined" ? navigator.connection : null;
  if (!c) return false;
  return (
    c.effectiveType === "slow-2g" ||
    c.effectiveType === "2g" ||
    (typeof c.rtt === "number" && c.rtt >= 1000)
  );
};

/**
 * The human-readable message from a backend error body, or null.
 * Handles the API's `{ errors: [{ message }] }` envelope, a plain
 * `{ message }`, ASP.NET validation problem details
 * (`{ title, errors: { Field: ["..."] } }`) and short plain-text bodies.
 * HTML error pages from a proxy are ignored.
 */
export const backendMessage = (data) => {
  if (!data) return null;
  if (typeof data === "string") {
    const text = data.trim();
    return text && text.length <= 300 && !text.startsWith("<") ? text : null;
  }
  if (typeof data !== "object") return null;

  const list = Array.isArray(data.errors)
    ? data.errors
    : Array.isArray(data.Errors)
    ? data.Errors
    : null;
  const first = list?.[0];
  const fromList =
    typeof first === "string" ? first : first?.message || first?.Message;

  const dict =
    data.errors && !Array.isArray(data.errors) && typeof data.errors === "object"
      ? data.errors
      : null;
  const fromDict = dict ? Object.values(dict).flat().find(Boolean) : null;

  const message =
    fromList ||
    data.message ||
    data.Message ||
    (typeof fromDict === "string" ? fromDict : null) ||
    data.detail ||
    data.title;
  return typeof message === "string" && message.trim() ? message.trim() : null;
};

/**
 * Checks whether the internet works at all by fetching a tiny file from the
 * site that served this app (not the API). If that loads but the API did not,
 * the problem is the API server, not the user's connection.
 * Resolves to { reachable, ms }.
 */
export const probeInternet = async (timeoutMs = 8000) => {
  if (isOffline()) return { reachable: false, ms: 0 };
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller && setTimeout(() => controller.abort(), timeoutMs);
  const started = Date.now();
  try {
    await fetch(`${window.location.origin}/robots.txt?probe=${started}`, {
      cache: "no-store",
      signal: controller?.signal,
    });
    return { reachable: true, ms: Date.now() - started };
  } catch (e) {
    return { reachable: false, ms: Date.now() - started };
  } finally {
    clearTimeout(timer);
  }
};

const result = (kind, title, message, retryable) => ({
  kind,
  title,
  message,
  retryable,
});

/**
 * Builds the message for a request that failed for good (after retries).
 * `error` is a raw axios error. `timedOut` says whether any attempt timed out.
 */
export const describeFailure = async (error, { timedOut = false } = {}) => {
  if (isCanceledError(error)) {
    return result(ERROR_KIND.CANCELED, "", "", true);
  }

  if (isOffline()) {
    return result(
      ERROR_KIND.OFFLINE,
      "No internet connection",
      "Your device is offline. Turn on Wi-Fi or mobile data, then try again.",
      true
    );
  }

  const response = error?.response;
  if (response) {
    const status = response.status;
    const fromServer = backendMessage(response.data);

    if (status === 429) {
      return result(
        ERROR_KIND.TOO_MANY,
        "Too many attempts",
        fromServer ||
          "Too many sign-in attempts. Please wait a few minutes and try again.",
        false
      );
    }
    if (status >= 500 || status === 408) {
      const unavailable = [502, 503, 504].includes(status);
      return result(
        ERROR_KIND.SERVER,
        unavailable ? "Server temporarily unavailable" : "Server error",
        `${
          unavailable
            ? "The TPMready server is not responding right now (it may be restarting or under maintenance)."
            : "Something went wrong on the server, not on your side."
        } Please try again in a few minutes. (Error ${status})`,
        true
      );
    }
    return result(
      ERROR_KIND.REJECTED,
      "Sign-in failed",
      fromServer || `The server refused the request (error ${status}).`,
      false
    );
  }

  // No response at all: decide whether the internet, the speed, or the
  // server is to blame.
  const probe = await probeInternet();
  if (isOffline()) return describeFailure(error, { timedOut });

  const slowLink = isConnectionReportedSlow() || probe.ms > 2500;
  // Attempts that timed out (rather than failing at once) mean there is a
  // link, just one too slow to finish the request.
  if (timedOut && (slowLink || !probe.reachable)) {
    return result(
      ERROR_KIND.SLOW,
      "Internet connection too slow",
      "We tried several times, but your connection is too slow or unstable to finish signing in. Move to a place with better signal or switch networks, then try again — your details are kept.",
      true
    );
  }
  if (!probe.reachable) {
    return result(
      ERROR_KIND.NO_INTERNET,
      "No internet access",
      "Your device is connected to a network, but the internet isn't reachable. Check your Wi-Fi or mobile data, or move to a place with better signal.",
      true
    );
  }

  if (timedOut) {
    return result(
      ERROR_KIND.SERVER_SLOW,
      "Server is taking too long",
      "Your internet is working, but the TPMready server did not respond in time. Please try again in a few minutes.",
      true
    );
  }
  return result(
    ERROR_KIND.SERVER_UNREACHABLE,
    "Can't reach the TPMready server",
    "Your internet is working, but the server can't be reached. It may be down for maintenance, or blocked on this network. Please try again in a few minutes.",
    true
  );
};
